import { useMemo } from 'react';
import { UIMarket } from '@velocity-exchange/common';
import {
	MAJOR_MARKET_BASE_SYMBOLS,
	MAJOR_MARKETS_LABEL,
	OTHER_MARKETS_LABEL,
} from 'src/components/Modals/FeeTierModal/consts';
import { TakerFeeGroup } from 'src/components/Modals/FeeTierModal/types';
import useVelocityClient from 'src/hooks/useVelocityClient';

/**
 * Splits perp markets into the majors the fee schedule prices separately and
 * everything else, reading each side's taker surcharge
 * (`PerpMarket.takerFeeAddonTenthBps`) off chain.
 *
 * Always returns both groups, in table-column order. While every market shares
 * a surcharge the two columns simply show the same fee, so the table keeps its
 * shape when a surcharge is configured later.
 */
export default function useTakerFeeGroups(): TakerFeeGroup[] {
	const velocityClient = useVelocityClient();
	const perpMarkets = velocityClient?.getPerpMarketAccounts();

	// `getPerpMarketAccounts` builds a fresh array on every call, so memoise on
	// the market/surcharge pairs rather than the array identity.
	const signature = perpMarkets
		?.map((market) => `${market.marketIndex}:${market.takerFeeAddonTenthBps}`)
		.sort()
		.join();

	return useMemo(() => {
		const majorAddons: number[] = [];
		const otherAddons: number[] = [];

		perpMarkets?.forEach((market) => {
			const addons = isMajorMarket(market.marketIndex)
				? majorAddons
				: otherAddons;

			addons.push(market.takerFeeAddonTenthBps ?? 0);
		});

		return [
			{
				label: MAJOR_MARKETS_LABEL,
				addonTenthBps: prevailingAddon(majorAddons),
			},
			{
				label: OTHER_MARKETS_LABEL,
				addonTenthBps: prevailingAddon(otherAddons),
			},
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [signature]);
}

function isMajorMarket(marketIndex: number) {
	const baseAssetSymbol = UIMarket.perpMarkets.find(
		(market) => market.marketIndex === marketIndex
	)?.baseAssetSymbol;

	// A market missing from the configs cannot be matched, so it counts as other.
	return (
		baseAssetSymbol !== undefined &&
		MAJOR_MARKET_BASE_SYMBOLS.includes(baseAssetSymbol)
	);
}

/**
 * The surcharge to quote for a group. Its markets are expected to share one, so
 * this takes the most common (lowest wins a tie) and lets a misconfigured
 * outlier stay an outlier instead of skewing the whole column.
 */
function prevailingAddon(addons: number[]) {
	if (!addons.length) {
		return 0;
	}

	const countsByAddon = new Map<number, number>();

	addons.forEach((addon) =>
		countsByAddon.set(addon, (countsByAddon.get(addon) ?? 0) + 1)
	);

	const [mostCommon] = [...countsByAddon.entries()].sort(
		([addonA, countA], [addonB, countB]) => countB - countA || addonA - addonB
	);

	return mostCommon[0];
}
